// Venue-search VS1.4: "Items at the venue" in the venue editor. Items, kinds,
// sizes and labels come from the field dictionary (src/taxonomy); the other
// words from src/content/venueEditor.js. Rows, checks and saving: inventory.js.
import { useId } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import copy from '../../../content/venueEditor';
import { setFact } from '../../../store/hosted-venue';
import { fieldHelp, fieldLabel, listOptions } from '../../../taxonomy';
import { colors, Field, FieldGroup, FormLabel, Input, Select } from '../../styles';
import { ErrorText, Help, Indent, Intro, Pills, SectionTitle } from './editorStyles';
import {
  addRow,
  choiceId,
  hasItem,
  hasSize,
  isOther,
  itemOptions,
  kindOptions,
  letters,
  NOTE_MAX,
  OTHER_MAX,
  otherText,
  removeRow,
  rowsOf,
  sizeOptions,
  toggleItem,
  updateRow
} from './inventory';
import { parseCount } from './venueFacts';

const ItemCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  font-size: 14px;
  cursor: pointer;
`;

const Card = styled.div`
  border: 1px solid ${({ $error }) => ($error ? colors.errorColor : colors.lightBorder)};
  border-radius: 8px;
  padding: 12px;
  margin: 4px 0 12px;

  ${FieldGroup} {
    flex-wrap: wrap;
  }

  ${FieldGroup} > ${Field} {
    flex: 1 1 140px;
    margin-bottom: 8px;
  }
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 6px 0;
  font-size: 14px;
  color: ${colors.primaryButton};
  text-decoration: underline;
  cursor: pointer;
`;

const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const words = {
  count: 'inventory.count',
  kind: 'inventory.kind',
  size: 'inventory.size',
  pricing: 'inventory.pricing',
  note: 'inventory.note'
};

const TextField = ({ label, value, error, onChange, help, max }) => {
  const id = useId();
  return (
    <Field hasError={!!error} data-invalid={!!error}>
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <Input id={id} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
      {help && <Help>{help}</Help>}
      {error === 'tooLong' && <ErrorText>{copy.tooLong(max, letters(value))}</ErrorText>}
    </Field>
  );
};

const ChoiceField = ({ label, value, options, error, onChange }) => {
  const id = useId();
  return (
    <Field hasError={!!error} data-invalid={!!error}>
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <Select id={id} value={choiceId(value) || ''} onChange={(e) => onChange(e.target.value || undefined)}>
        <option value=""></option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </Select>
    </Field>
  );
};

const ItemRow = ({ row, index, removable, errors, country, onChange, onRemove }) => {
  const radioName = useId();
  const countId = useId();
  const error = (part) => errors[`inventory.${index}.${part}`];
  const hasError = ['item', 'count', 'kind', 'size', 'pricing', 'note'].some(error);
  // A choice that allows Other: picking Other keeps the text box's words.
  const setChoice = (part, id) => onChange({ [part]: id === 'other' ? { list: 'other', other: otherText(row[part]) } : id }, part);
  const setOther = (part, text) => onChange({ [part]: { list: 'other', other: text } }, part);
  const kinds = kindOptions(row);
  const sizes = sizeOptions(row, country);
  const item = choiceId(row.item);

  return (
    <Card $error={hasError}>
      {removable && (
        <CardHead>
          <span />
          <LinkButton type="button" onClick={onRemove}>
            {copy.inventory.remove}
          </LinkButton>
        </CardHead>
      )}
      {isOther(row.item) && (
        <TextField
          label={copy.inventory.otherName}
          value={otherText(row.item)}
          error={error('item')}
          max={OTHER_MAX}
          onChange={(text) => setOther('item', text)}
        />
      )}
      <FieldGroup>
        <Field hasError={!!error('count')} data-invalid={!!error('count')}>
          <FormLabel htmlFor={countId}>{fieldLabel(words.count)}</FormLabel>
          <Input
            id={countId}
            inputMode="numeric"
            value={row.count === undefined ? '' : String(row.count)}
            onChange={(e) => onChange({ count: parseCount(e.target.value) }, 'count')}
          />
          {error('count') && <ErrorText>{copy.inventory.errors.count}</ErrorText>}
        </Field>
        {kinds.length > 0 && (
          <ChoiceField label={fieldLabel(words.kind)} value={row.kind} options={kinds} error={error('kind')} onChange={(id) => setChoice('kind', id)} />
        )}
        {hasSize(row) && sizes.length > 0 && (
          <ChoiceField label={fieldLabel(words.size)} value={row.size} options={sizes} error={error('size')} onChange={(id) => setChoice('size', id)} />
        )}
      </FieldGroup>
      {item === 'tablecloths' && sizes.length > 0 && <Help style={{ margin: '-4px 0 8px' }}>{copy.inventory.clothSize}</Help>}
      {isOther(row.kind) && (
        <TextField
          label={copy.inventory.otherKind}
          value={otherText(row.kind)}
          error={error('kind')}
          max={OTHER_MAX}
          onChange={(text) => setOther('kind', text)}
        />
      )}
      {error('kind') === 'missing' && <ErrorText style={{ margin: '-8px 0 8px' }}>{copy.inventory.errors.kind}</ErrorText>}
      {isOther(row.size) && (
        <TextField
          label={copy.inventory.otherSize}
          value={otherText(row.size)}
          error={error('size')}
          max={OTHER_MAX}
          onChange={(text) => setOther('size', text)}
        />
      )}
      {error('size') === 'missing' && <ErrorText style={{ margin: '-8px 0 8px' }}>{copy.inventory.errors.size}</ErrorText>}
      <fieldset style={{ border: 'none', margin: '0 0 8px', padding: 0 }} data-invalid={!!error('pricing')}>
        <FormLabel as="legend">{fieldLabel(words.pricing)}</FormLabel>
        <Pills $error={!!error('pricing')} style={{ marginTop: 8, flexWrap: 'wrap' }}>
          {listOptions('itemPricing').map(({ id, label }) => (
            <label key={id}>
              <input type="radio" name={radioName} checked={row.pricing === id} onChange={() => onChange({ pricing: id }, 'pricing')} />
              {label}
            </label>
          ))}
        </Pills>
        <Help>{fieldHelp(words.pricing)}</Help>
        {error('pricing') && <ErrorText>{copy.inventory.errors.pricing}</ErrorText>}
      </fieldset>
      <TextField
        label={copy.inventory.note}
        value={row.note || ''}
        error={error('note')}
        max={NOTE_MAX}
        help={fieldHelp(words.note)}
        onChange={(text) => onChange({ note: text || undefined }, 'note')}
      />
    </Card>
  );
};

export const InventorySection = ({ form, errors }) => {
  const dispatch = useDispatch();
  const rows = rowsOf(form.facts.inventory);
  const country = form.address_country;
  // clear: the error keys this change can fix; "inventory." when rows move.
  const set = (next, clear) => dispatch(setFact({ id: 'inventory', value: next.length ? next : undefined, clear }));
  const onItemError = (index) => errors[`inventory.${index}.item`] === 'missing';

  return (
    <>
      <SectionTitle>{copy.inventory.title}</SectionTitle>
      <Intro>{copy.inventory.intro}</Intro>
      {itemOptions().map(({ id, label }) => {
        const checked = hasItem(rows, id);
        const indexes = rows.map((row, index) => (choiceId(row.item) === id ? index : -1)).filter((index) => index >= 0);
        return (
          <div key={id}>
            <ItemCheck>
              <input type="checkbox" checked={checked} onChange={(e) => set(toggleItem(rows, id, e.target.checked), 'inventory.')} />
              {label}
            </ItemCheck>
            {checked && (
              <Indent>
                {indexes.map((index, n) => (
                  <div key={index}>
                    <ItemRow
                      row={rows[index]}
                      index={index}
                      removable={n > 0}
                      errors={errors}
                      country={country}
                      onChange={(changes, part) => set(updateRow(rows, index, changes, country), [`inventory.${index}.${part}`])}
                      onRemove={() => set(removeRow(rows, index), 'inventory.')}
                    />
                    {onItemError(index) && <ErrorText style={{ margin: '-8px 0 8px' }}>{copy.inventory.errors.item}</ErrorText>}
                  </div>
                ))}
                <LinkButton type="button" onClick={() => set(addRow(rows, id), 'inventory.')}>
                  {copy.inventory.addAnother}
                </LinkButton>
              </Indent>
            )}
          </div>
        );
      })}
    </>
  );
};
